/**
 * The business's own words in further languages (S36, Martin 31.10.2026): everything on a card that
 * is not a dish or a drink – the card's title, subtitle and notes, its sections and courses, the
 * labels of prices, categories, tags, own allergens and additives, the texts of the catalogue (S14),
 * an own footer. One dictionary per business, by the German text: „Vorspeisen“ translated once holds
 * on every card. A changed German text is another entry, so its translation is missing again; no
 * fingerprint needed. By hand or by the model; what someone wrote by hand the model never overwrites.
 */
import type { Allergen } from "./allergens.ts";
import type { CardText } from "./texts.ts";
import type { Dish } from "./dishes.ts";
import { DEFAULT_ALLERGENS } from "./allergens.ts";
import { isDietTag } from "./dishes.ts";
import { isLang } from "./translation.ts";
import type { Lang } from "./translation.ts";
import { menuView } from "./menu.ts";
import type { MenuInput, MenuView } from "./menu.ts";

export interface Phrase {
  text: string;
  /** By hand, by the model, or by the model and looked through („Passt“, S61): only „ai“ is still to be checked. */
  by: "hand" | "ai" | "checked";
}
/** By the German text (`phraseKey`), its translations. */
export type Phrases = Record<string, Partial<Record<Lang, Phrase>>>;

export const PHRASE_LIMITS = { key: 800, text: 800, count: 4000 } as const;

import { phraseKey, phraseOf } from "./phrase-base.ts";
export { phraseKey };

/** Whether a text has words to translate, not only numbers, amounts and units („0,3 l“, „250 g“, „½“). */
export function hasWords(s: string): boolean {
  const rest = phraseKey(s)
    .replace(/(^|[\s\d])(l|cl|dl|ml|g|kg|cm|mm)\.?(?=$|[\s/|,;)])/gi, "$1")
    .replace(/[^\p{L}]/gu, "");
  return rest.length >= 2;
}

/** A text in a language: the business's translation, else the base vocabulary's, else the German as it is. */
export function phraseIn(text: string, lang: Lang, phrases: Phrases): string {
  if (!text) return text;
  return phraseOf(text, lang, phrases)?.text || text;
}

/** Whether a text still needs a translation into the language: neither the business's nor the base's. */
export const phraseMissing = (text: string, lang: Lang, phrases: Phrases) => hasWords(text) && !phraseOf(text, lang, phrases);

/** One of the 14 allergens as the default names it: its name is official in every language, not the dictionary's. */
export const isDefaultAllergen = (a: Pick<Allergen, "code" | "name">) => DEFAULT_ALLERGENS.some((d) => d.kind === "allergen" && d.code === a.code && d.name === a.name);

/** The texts of a card that the dictionary translates, in their order, each once. */
export function viewPhrases(view: MenuView): string[] {
  const out: string[] = [];
  const add = (...texts: (string | undefined)[]) => {
    for (const t of texts) if (t && hasWords(t)) out.push(phraseKey(t));
  };
  add(view.title, view.subtitle, view.note);
  for (const s of view.sections) {
    add(s.title, s.note, s.priceLabel, s.priceHead, ...(s.variants ?? []).map((v) => v.label));
    for (const i of s.items) {
      add(i.course, i.sub, i.note, i.priceLabel, ...i.variants.map((v) => v.label), ...(i.cols ?? []));
      if (i.text) add(i.name, i.description);
      add(...i.tags.filter((t) => !isDietTag(t)));
    }
  }
  add(...view.legend.filter((a) => !isDefaultAllergen(a)).map((a) => a.name));
  return [...new Set(out)];
}

/** Where a text of the dictionary comes from, for the list in the catalogue. */
export type PhraseGroup = "card" | "category" | "tag" | "allergen" | "text" | "price" | "footer";
export const PHRASE_GROUP_LABELS: Record<PhraseGroup, string> = {
  card: "Speisekarten",
  category: "Kategorien",
  tag: "Tags",
  allergen: "Allergene und Zusatzstoffe",
  text: "Textbausteine",
  price: "Preisbezeichnungen",
  footer: "Fuß",
};

export interface PhraseSource {
  dishes: readonly Dish[];
  allergens: readonly Allergen[];
  texts: readonly CardText[];
  categories: readonly string[];
  drinkCategories: readonly string[];
  tags: readonly string[];
  drinkTags: readonly string[];
  menus: readonly MenuInput[];
  tagIcons: Readonly<Record<string, string>>;
  footer: string;
}

/**
 * Every text of the business the dictionary translates, each once, with where it first comes from:
 * the cards (as they are shown), then the catalogue, the labels of the dishes' prices, the footer.
 */
export function businessPhrases(src: PhraseSource): { text: string; group: PhraseGroup }[] {
  const out = new Map<string, PhraseGroup>();
  const add = (group: PhraseGroup, ...texts: string[]) => {
    for (const t of texts) if (t && hasWords(t) && !out.has(phraseKey(t))) out.set(phraseKey(t), group);
  };
  const byId = new Map(src.dishes.map((d) => [d.id, d]));
  const texts = new Map(src.texts.map((t) => [t.id, t]));
  for (const m of src.menus) add("card", ...viewPhrases(menuView(m, byId, src.allergens, texts, src.tagIcons)));
  add("category", ...src.categories, ...src.drinkCategories);
  add("tag", ...[...src.tags, ...src.drinkTags].filter((t) => !isDietTag(t)));
  add("allergen", ...src.allergens.filter((a) => !isDefaultAllergen(a)).map((a) => a.name));
  add("text", ...src.texts.flatMap((t) => [t.title, t.body]));
  add("price", ...src.dishes.filter((d) => !d.archived).flatMap((d) => [d.priceLabel, ...d.variants.map((v) => v.label)]));
  add("footer", src.footer);
  return [...out].map(([text, group]) => ({ text, group }));
}

/**
 * Translations as sent from the app or the model: by the German text, the language's text; an empty
 * one takes it away. Or what is wrong with them.
 */
export function cleanPhraseInput(raw: unknown): { value: Map<string, string> } | { error: string } {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { error: "Die Übersetzungen passen nicht." };
  const out = new Map<string, string>();
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const key = phraseKey(k);
    if (!key || typeof v !== "string") return { error: "Die Übersetzungen passen nicht." };
    if (key.length > PHRASE_LIMITS.key) return { error: "Ein deutscher Text ist zu lang." };
    const text = phraseKey(v);
    if (text.length > PHRASE_LIMITS.text) return { error: `Eine Übersetzung ist zu lang (höchstens ${PHRASE_LIMITS.text} Zeichen).` };
    out.set(key, text);
  }
  return { value: out };
}

/** The dictionary as stored, field by field; only known languages. */
export function phrasesOf(raw: unknown): Phrases {
  const out: Phrases = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [k, langs] of Object.entries(raw as Record<string, unknown>)) {
    if (!langs || typeof langs !== "object") continue;
    const entry: Partial<Record<Lang, Phrase>> = {};
    for (const [lang, p] of Object.entries(langs as Record<string, unknown>)) {
      const x = p as { text?: unknown; by?: unknown } | null;
      if (!isLang(lang) || !x || typeof x.text !== "string" || !x.text) continue;
      entry[lang] = { text: x.text.slice(0, PHRASE_LIMITS.text), by: x.by === "ai" || x.by === "checked" ? x.by : "hand" };
    }
    if (Object.keys(entry).length) out[phraseKey(k)] = entry;
  }
  return out;
}

/**
 * The dictionary with translations into one language set: by hand they replace what is there, an
 * empty one takes it away; by the model only where nothing is (S36: never over a hand's).
 */
export function withPhrases(phrases: Phrases, lang: Lang, made: ReadonlyMap<string, string>, by: Phrase["by"]): Phrases {
  const out: Phrases = { ...phrases };
  for (const [key, text] of made) {
    const entry = { ...out[key] };
    if (by === "ai" && entry[lang]) continue;
    if (text) entry[lang] = { text, by };
    else delete entry[lang];
    if (Object.keys(entry).length) out[key] = entry;
    else delete out[key];
  }
  return out;
}

/** „Passt“ (S61): translations by the model into the language looked through; the others stay as they are. */
export function phrasesChecked(phrases: Phrases, lang: Lang, texts: Iterable<string>): Phrases {
  const out: Phrases = { ...phrases };
  for (const t of texts) {
    const key = phraseKey(t);
    const p = out[key]?.[lang];
    if (p?.by === "ai") out[key] = { ...out[key], [lang]: { ...p, by: "checked" } };
  }
  return out;
}

/**
 * German texts renamed (S61): their translations go along where the new text has none yet. Renamed in
 * a list („Vorspeise“ → „Vorspeisen“) the sense may have changed, so each is to be checked again
 * (`review`: by the model, not looked through). Changed by a correction taken (`corrected`, S47: the
 * sense is the same) a translation stays as it was; one by the model not looked through yet is left
 * behind, so that it is made anew from the corrected German.
 */
export function carriedPhrases(phrases: Phrases, pairs: Iterable<readonly [string, string]>, how: "review" | "corrected"): Phrases {
  const out: Phrases = { ...phrases };
  for (const [from, to] of pairs) {
    const a = phraseKey(from);
    const b = phraseKey(to);
    if (!a || !b || a === b || !out[a]) continue;
    const next = { ...out[b] };
    for (const [lang, p] of Object.entries(out[a]!) as [Lang, Phrase][]) {
      if (next[lang]?.text) continue;
      if (how === "review") next[lang] = { text: p.text, by: "ai" };
      else if (p.by !== "ai") next[lang] = p;
    }
    if (Object.keys(next).length) out[b] = next;
  }
  return out;
}

/** Too many entries: the error, else null. */
export const phrasesTooMany = (phrases: Phrases): string | null => (Object.keys(phrases).length > PHRASE_LIMITS.count ? `Zu viele Übersetzungen (höchstens ${PHRASE_LIMITS.count}).` : null);
